
import React from 'react';
import { Link } from 'react-router-dom';
import {
  FileText,
  CreditCard,
  Shield,
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Scale,
  Users,
  Lock,
  FileCheck,
  Ban,
  Gavel,
  Download,
  Eye,
  ShoppingCart
} from 'lucide-react';

const CGVPage = () => {
  return (
    <div className="min-h-screen bg-[#050505] pt-24 px-4">
      <div className="max-w-4xl mx-auto py-16">

        {/* HEADER */}
        <div className="flex items-center gap-4 mb-8">
          <FileText className="w-12 h-12 text-yellow-400" />

          <h1
            className="text-4xl font-bold text-white"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Conditions Générales de Vente
          </h1>
        </div>

        <p className="text-gray-400 mb-4 text-lg">
          Dernière mise à jour : Mars 2026
        </p>

        <p className="text-gray-500 mb-12 text-sm">
          Conditions applicables aux achats de vidéos, contenus numériques,
          produits et services proposés sur la plateforme Titelli.
        </p>

        <div className="space-y-12 text-gray-400">

          {/* ARTICLE 1 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-yellow-500 text-black flex items-center justify-center text-sm font-bold">
                1
              </span>
              Dispositions générales
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                <strong className="text-white">1.1 Objet</strong><br />
                Les présentes Conditions Générales de Vente (ci-après
                « CGV ») régissent l'ensemble des achats effectués sur la
                plateforme Titelli, notamment l'achat de vidéos, contenus
                numériques, produits, abonnements et prestations proposés
                sur la plateforme.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">1.2 Acceptation</strong><br />
                Toute commande effectuée sur Titelli implique l'acceptation
                pleine, entière et sans réserve des présentes CGV.
                Avant chaque achat, le Client doit pouvoir consulter les
                présentes CGV et confirmer leur acceptation.
              </p>

              <p className="leading-relaxed">
                En validant une commande, le Client reconnaît avoir pris
                connaissance des présentes CGV et les accepter.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">1.3 Modification</strong><br />
                Titelli peut modifier les présentes CGV lorsque cela est
                nécessaire. La version applicable à une commande est celle
                acceptée par le Client au moment de la conclusion de cette
                commande.
              </p>

            </div>
          </section>

          {/* ARTICLE 2 */}
          <section className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700">

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-yellow-500 text-black flex items-center justify-center text-sm font-bold">
                2
              </span>
              Définitions
            </h2>

            <div className="pl-11 space-y-3">

              <p>
                <strong className="text-yellow-400">Plateforme :</strong>{' '}
                désigne le site internet Titelli et les services numériques
                associés.
              </p>

              <p>
                <strong className="text-yellow-400">Client :</strong>{' '}
                toute personne effectuant un achat ou utilisant un contenu
                disponible sur Titelli.
              </p>

              <p>
                <strong className="text-yellow-400">Vendeur / Créateur :</strong>{' '}
                personne ou entreprise proposant un produit ou contenu sur
                Titelli.
              </p>

              <p>
                <strong className="text-yellow-400">Produit :</strong>{' '}
                tout produit physique ou numérique proposé à la vente.
              </p>

              <p>
                <strong className="text-yellow-400">Vidéo :</strong>{' '}
                tout contenu audiovisuel vendu, accessible ou distribué
                via Titelli.
              </p>

              <p>
                <strong className="text-yellow-400">Contenu numérique :</strong>{' '}
                toute vidéo, image, fichier, document, média ou autre contenu
                accessible sous forme numérique.
              </p>

              <p>
                <strong className="text-yellow-400">Commande :</strong>{' '}
                acte par lequel le Client valide l'achat d'un produit ou
                contenu proposé sur la plateforme.
              </p>

            </div>
          </section>

          {/* ARTICLE 3 */}
          <section>

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Users className="w-8 h-8 text-yellow-400" />
              Article 3 - Compte utilisateur
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                <strong className="text-white">3.1 Création</strong><br />
                Certains achats et fonctionnalités peuvent nécessiter un
                compte utilisateur.
              </p>

              <p className="leading-relaxed">
                Le Client s'engage à fournir des informations exactes,
                complètes et à jour.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">3.2 Sécurité</strong><br />
                Le Client est responsable de la confidentialité de ses
                identifiants et de toute utilisation effectuée depuis son
                compte.
              </p>

              <p className="leading-relaxed">
                Le partage d'un compte dans le but de permettre à des tiers
                d'accéder à des contenus payants peut entraîner la suspension
                du compte.
              </p>

            </div>
          </section>

          {/* ARTICLE 4 */}
          <section>

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <ShoppingCart className="w-8 h-8 text-yellow-400" />
              Article 4 - Produits et commandes
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                <strong className="text-white">4.1 Description</strong><br />
                Les caractéristiques essentielles, le prix et les conditions
                applicables à chaque produit ou contenu sont présentés avant
                la validation de la commande.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">4.2 Validation</strong><br />
                Le Client doit vérifier les informations de sa commande avant
                de la confirmer.
              </p>

              <p className="leading-relaxed">
                La validation de la commande constitue un engagement de
                paiement portant sur les produits ou contenus sélectionnés.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">4.3 Confirmation</strong><br />
                Une confirmation de commande peut être adressée au Client
                par voie électronique.
              </p>

            </div>
          </section>

          {/* ARTICLE 5 */}
          <section className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700">

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-yellow-400" />
              Article 5 - Prix et paiement
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                <strong className="text-white">5.1 Prix</strong><br />
                Les prix sont indiqués en francs suisses (CHF), sauf
                indication contraire.
              </p>

              <p className="leading-relaxed">
                Le prix applicable est celui affiché au moment de la
                validation de la commande.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">5.2 Paiement</strong><br />
                Les paiements peuvent être effectués par les moyens de
                paiement proposés sur la plateforme, notamment par carte
                bancaire ou tout autre moyen indiqué lors de la commande.
              </p>

              <p className="leading-relaxed">
                Le paiement peut être traité par un prestataire de paiement
                tiers sécurisé tel que Stripe.
              </p>

              <p className="leading-relaxed">
                Titelli ne demande jamais au Client de communiquer ses
                données bancaires complètes par e-mail ou par message.
              </p>

            </div>
          </section>

          {/* ARTICLE 6 - ACHAT ET REMBOURSEMENT */}
          <section className="bg-yellow-900/20 rounded-2xl p-6 border border-yellow-700/30">

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <RefreshCw className="w-8 h-8 text-yellow-400" />
              Article 6 - Commandes, annulation et remboursement
            </h2>

            <div className="pl-11 space-y-5">

              <p className="leading-relaxed">
                <strong className="text-white">
                  6.1 Absence de droit général de rétractation
                </strong><br />
                Le droit suisse ne prévoit pas de droit général de
                rétractation pour les achats effectués en ligne. Titelli
                n'accorde donc pas de droit général d'annulation ou de retour
                après validation d'une commande, sous réserve des droits
                impératifs prévus par la loi.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  6.2 Commande définitive
                </strong><br />
                Dès lors qu'une commande a été validée et que le paiement a
                été accepté, celle-ci est considérée comme définitive.
              </p>

              <p className="leading-relaxed">
                Pour les contenus numériques, notamment les vidéos, le Client
                reconnaît que l'accès au contenu peut être fourni rapidement
                après le paiement.
              </p>

              <div className="bg-black/30 rounded-xl p-5 border border-yellow-700/30">

                <div className="flex items-start gap-3">

                  <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />

                  <p className="leading-relaxed text-gray-300">
                    <strong className="text-white">
                      En validant une commande, le Client reconnaît que
                      l'achat est définitif et qu'il ne peut pas demander
                      l'annulation ou le remboursement simplement parce qu'il
                      a changé d'avis, sous réserve des droits légaux
                      impératifs applicables.
                    </strong>
                  </p>

                </div>

              </div>

              <p className="leading-relaxed">
                <strong className="text-white">
                  6.3 Contenus numériques
                </strong><br />
                Lorsqu'un contenu numérique est rendu accessible au Client
                après paiement, aucun remboursement ne sera effectué en cas
                de simple changement d'avis, de non-utilisation du contenu ou
                de téléchargement ou consultation du contenu.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  6.4 Produit défectueux ou non conforme
                </strong><br />
                Le présent article ne limite pas les droits dont le Client
                pourrait disposer en vertu des dispositions légales
                impératives applicables en cas de défaut, de non-conformité
                substantielle ou de manquement de Titelli à ses obligations.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  6.5 Remboursement exceptionnel
                </strong><br />
                Tout remboursement accordé à titre exceptionnel par Titelli
                ne constitue pas un droit acquis pour les commandes futures.
              </p>

            </div>
          </section>

          {/* ARTICLE 7 - VIDEOS */}
          <section className="bg-red-900/20 rounded-2xl p-6 border border-red-700/30">

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Eye className="w-8 h-8 text-red-400" />
              Article 7 - Utilisation des vidéos et contenus numériques
            </h2>

            <div className="pl-11 space-y-5">

              <p className="leading-relaxed">
                <strong className="text-white">
                  7.1 Licence personnelle
                </strong><br />
                L'achat d'une vidéo ou d'un contenu numérique donne au Client
                un droit d'accès personnel, limité, non exclusif et
                non transférable au contenu acheté.
              </p>

              <p className="leading-relaxed">
                L'achat d'une vidéo ne constitue en aucun cas une cession des
                droits d'auteur, droits voisins, droits à l'image ou autres
                droits de propriété intellectuelle attachés au contenu.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  7.2 Interdiction de reproduction
                </strong><br />
                Il est strictement interdit, sans autorisation écrite
                préalable du titulaire des droits, de :
              </p>

              <ul className="list-disc list-inside space-y-2 ml-4">

                <li>
                  télécharger, copier ou enregistrer une vidéo lorsque cette
                  fonctionnalité n'est pas expressément autorisée ;
                </li>

                <li>
                  reproduire ou dupliquer tout ou partie d'un contenu acheté ;
                </li>

                <li>
                  partager une vidéo ou un lien d'accès privé avec des tiers ;
                </li>

                <li>
                  republier une vidéo sur un autre site internet ;
                </li>

                <li>
                  publier une vidéo sur les réseaux sociaux ;
                </li>

                <li>
                  distribuer une vidéo via Telegram, WhatsApp, Discord ou
                  toute autre plateforme ;
                </li>

                <li>
                  vendre, louer, donner ou transférer un contenu acheté ;
                </li>

                <li>
                  mettre un contenu à disposition du public ;
                </li>

                <li>
                  utiliser un contenu à des fins commerciales sans
                  autorisation ;
                </li>

                <li>
                  supprimer ou masquer les mentions de propriété ou
                  d'identification présentes dans un contenu ;
                </li>

                <li>
                  contourner les protections techniques ou systèmes
                  d'authentification mis en place par Titelli.
                </li>

              </ul>

              <p className="leading-relaxed">
                <strong className="text-white">
                  7.3 Partage et redistribution
                </strong><br />
                Toute redistribution, republication, revente ou mise à
                disposition d'un contenu acheté est interdite, même lorsque
                le Client a lui-même acheté le contenu.
              </p>

              <p className="leading-relaxed">
                Le fait d'avoir payé un contenu ne donne donc pas au Client
                le droit de le transmettre à d'autres personnes.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  7.4 Captures et enregistrements
                </strong><br />
                Le Client s'interdit également d'utiliser un dispositif ou un
                logiciel permettant de contourner les protections mises en
                place afin de capturer, enregistrer ou extraire un contenu
                numérique lorsque cette utilisation est interdite par les
                présentes CGV ou par la loi.
              </p>

            </div>
          </section>

          {/* ARTICLE 8 - PROPRIETE INTELLECTUELLE */}
          <section>

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <FileCheck className="w-8 h-8 text-yellow-400" />
              Article 8 - Propriété intellectuelle
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                <strong className="text-white">
                  8.1 Droits sur la plateforme
                </strong><br />
                Le design, les interfaces, logos, marques, textes,
                illustrations, logiciels, éléments graphiques et autres
                éléments de la plateforme sont protégés par les dispositions
                applicables en matière de propriété intellectuelle.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  8.2 Droits sur les vidéos
                </strong><br />
                Les vidéos et contenus proposés sur Titelli restent la
                propriété de leurs auteurs, créateurs, vendeurs ou ayants
                droit, sauf indication contraire.
              </p>

              <p className="leading-relaxed">
                Aucun achat ne transfère automatiquement les droits d'auteur
                ou autres droits de propriété intellectuelle au Client.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  8.3 Utilisation non autorisée
                </strong><br />
                Toute utilisation dépassant le simple accès personnel
                autorisé peut constituer une violation des droits du
                titulaire et entraîner des mesures de suspension, retrait
                du contenu ou action judiciaire lorsque cela est justifié.
              </p>

            </div>
          </section>

          {/* ARTICLE 9 */}
          <section className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700">

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Shield className="w-8 h-8 text-yellow-400" />
              Article 9 - Sécurité et lutte contre la fraude
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                Il est interdit de tenter de contourner les systèmes de
                sécurité, d'authentification, de paiement ou de contrôle
                d'accès de Titelli.
              </p>

              <ul className="list-disc list-inside space-y-2 ml-4">

                <li>utiliser un moyen de paiement frauduleux ;</li>
                <li>utiliser le compte d'un autre utilisateur ;</li>
                <li>contourner les restrictions d'accès ;</li>
                <li>exploiter une faille technique à des fins personnelles ;</li>
                <li>revendre un accès à un contenu ;</li>
                <li>partager massivement des identifiants ;</li>
                <li>tenter d'obtenir gratuitement un contenu payant.</li>

              </ul>

              <p className="leading-relaxed">
                Toute activité suspecte peut entraîner une suspension
                immédiate du compte et, lorsque cela est nécessaire, la prise
                de mesures judiciaires.
              </p>

            </div>
          </section>

          {/* ARTICLE 10 */}
          <section>

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Ban className="w-8 h-8 text-red-400" />
              Article 10 - Utilisations interdites
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                Il est interdit d'utiliser Titelli pour :
              </p>

              <ul className="list-disc list-inside space-y-2 ml-4">

                <li>publier du contenu illégal ;</li>
                <li>usurper l'identité d'une autre personne ;</li>
                <li>frauder un autre utilisateur ;</li>
                <li>diffuser des logiciels malveillants ;</li>
                <li>attaquer ou perturber la plateforme ;</li>
                <li>scraper ou aspirer massivement les contenus ;</li>
                <li>contourner les systèmes de sécurité ;</li>
                <li>redistribuer des contenus payants ;</li>
                <li>revendre des accès sans autorisation ;</li>
                <li>utiliser les contenus à des fins commerciales sans licence.</li>

              </ul>

            </div>
          </section>

          {/* ARTICLE 11 */}
          <section>

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Lock className="w-8 h-8 text-yellow-400" />
              Article 11 - Protection des données
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                Les données personnelles sont traitées conformément à la
                Politique de confidentialité de Titelli et à la législation
                suisse applicable en matière de protection des données.
              </p>

              <p className="leading-relaxed">
                Les données nécessaires au traitement des commandes, à la
                gestion des comptes, aux paiements et à la sécurité peuvent
                être conservées conformément aux obligations légales
                applicables.
              </p>

              <p className="leading-relaxed">
                Pour plus d'informations, le Client peut consulter la
                <Link
                  to="/politique-confidentialite"
                  className="text-yellow-400 hover:text-yellow-300 ml-1"
                >
                  Politique de Confidentialité
                </Link>.
              </p>

            </div>
          </section>

          {/* ARTICLE 12 */}
          <section>

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Users className="w-8 h-8 text-yellow-400" />
              Article 12 - Suspension et résiliation
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                Titelli peut suspendre ou résilier un compte lorsqu'un
                utilisateur viole les présentes CGV, notamment en cas de
                fraude, de partage non autorisé de contenus ou de tentative
                de contournement des protections de la plateforme.
              </p>

              <p className="leading-relaxed">
                La suspension d'un compte peut entraîner la perte de l'accès
                aux fonctionnalités de la plateforme et aux contenus lorsque
                cette mesure est justifiée par une violation des présentes
                conditions.
              </p>

              <p className="leading-relaxed">
                Cette disposition ne prive pas le Client de ses droits
                impératifs prévus par la loi.
              </p>

            </div>
          </section>

          {/* ARTICLE 13 */}
          <section className="bg-blue-900/20 rounded-2xl p-6 border border-blue-700/30">

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Gavel className="w-8 h-8 text-blue-400" />
              Article 13 - Droit applicable et litiges
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                <strong className="text-white">
                  13.1 Droit applicable
                </strong><br />
                Les présentes CGV sont régies par le droit suisse, sous
                réserve des dispositions impératives éventuellement
                applicables au Client.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  13.2 Résolution amiable
                </strong><br />
                En cas de difficulté, les parties sont encouragées à
                rechercher une solution amiable avant toute procédure
                judiciaire.
              </p>

              <p className="leading-relaxed">
                <strong className="text-white">
                  13.3 Juridiction
                </strong><br />
                Sous réserve des règles impératives applicables aux
                consommateurs, les tribunaux compétents sont ceux désignés
                conformément au droit suisse.
              </p>

            </div>
          </section>

          {/* ARTICLE 14 */}
          <section>

            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-yellow-400" />
              Article 14 - Acceptation lors de l'achat
            </h2>

            <div className="pl-11 space-y-4">

              <p className="leading-relaxed">
                Avant de finaliser une commande, le Client doit avoir accès
                aux présentes CGV et être informé que la validation de la
                commande implique leur acceptation.
              </p>

              <p className="leading-relaxed">
                En cochant la case d'acceptation des CGV et en validant son
                paiement, le Client reconnaît :
              </p>

              <ul className="list-disc list-inside space-y-2 ml-4">

                <li>
                  avoir lu les présentes Conditions Générales de Vente ;
                </li>

                <li>
                  les accepter sans réserve ;
                </li>

                <li>
                  comprendre que l'achat d'un contenu numérique est destiné
                  à un usage personnel ;
                </li>

                <li>
                  comprendre que les contenus achetés ne peuvent pas être
                  redistribués ou republiés ;
                </li>

                <li>
                  comprendre que les commandes sont définitives et ne sont
                  pas annulables simplement en raison d'un changement d'avis,
                  sous réserve des droits légaux impératifs applicables.
                </li>

              </ul>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-5 mt-5">

                <p className="text-yellow-300 font-semibold">
                  En validant une commande, le Client confirme avoir pris
                  connaissance et accepté les présentes CGV.
                </p>

              </div>

            </div>
          </section>

          {/* CONTACT */}
      

        </div>

        {/* FOOTER LINKS */}
        <div className="mt-16 pt-8 border-t border-gray-800 flex flex-wrap gap-6 text-sm text-gray-500">

          <Link
            to="/mentions-legales"
            className="hover:text-yellow-400 transition-colors"
          >
            Mentions Légales
          </Link>

          <Link
            to="/politique-confidentialite"
            className="hover:text-yellow-400 transition-colors"
          >
            Politique de Confidentialité
          </Link>


        </div>

        <p className="mt-8 text-xs text-gray-600 text-center">
          © 2026 Ts Lexata - Tous droits réservés - Version CGV 3.0 - Mars 2026
        </p>

      </div>
    </div>
  );
};

export default CGVPage;

